import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAvatarUrl } from "src/Common/common";
import { STATUS_META } from "src/Pages/TimeOff/TimeOff";

// Per-type presentation for calendar chips/legend: a pill icon (served from
// /public) plus a soft pill colour and the matching text colour. Keyed by the
// same type ids as TimeOff's TYPE_META (0 Vacation, 1 Sick, 2 Medical
// Appointment, 3 Other).
export const TYPE_UI = {
  0: {
    label: "Vacation",
    icon: "/images/calendar/timeoff/pill_vacation.svg",
    chip: "bg-[#EAF3FF] text-[#0058E5]",
    dot: "bg-[#3B5BFE]",
  },
  1: {
    label: "Sick",
    icon: "/images/calendar/timeoff/pill_sick.svg",
    chip: "bg-[#FFEDD5] text-[#C2410C]",
    dot: "bg-[#F4A52E]",
  },
  2: {
    label: "Medical Appointment",
    icon: "/images/calendar/timeoff/pill_medical_appointment.svg",
    chip: "bg-[#F3E8FF] text-[#7E22CE]",
    dot: "bg-[#A855F7]",
  },
  3: {
    label: "Other",
    icon: "/images/calendar/timeoff/pill_other.svg",
    chip: "bg-[#EEF0F9] text-[#1A2332]",
    dot: "bg-[#94A3B8]",
  },
};

export const typeUi = (type) => TYPE_UI[type] ?? TYPE_UI[3];

// Approved (1/2) -> green "Approved"; pending (0) -> amber "Pending"; else the
// base label. Used by the rich request card.
export const statusUi = (status) => {
  if (status === 1 || status === 2) {
    return { label: "Approved", className: "bg-[#DCFCE7] text-[#16A34A]" };
  }
  if (status === 0) {
    return { label: "Pending", className: "bg-[#FEF3C7] text-[#D97706]" };
  }
  const meta = STATUS_META[status];
  return { label: meta?.label || "—", className: "bg-[#F1F5F9] text-[#475569]" };
};

// Horizontal legend shown above the calendar grid.
export function TimeOffLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pb-4">
      {Object.values(TYPE_UI).map(({ label, dot }) => (
        <div key={label} className="flex items-center gap-2">
          <span className={`inline-block w-3 h-3 rounded-[3px] ${dot}`} />
          <span className="font-OpenSansRegular text-[13px] leading-[18px] text-[#475569]">{label}</span>
        </div>
      ))}
    </div>
  );
}

// "Sarah Kennedy" -> "Sarah K." ; falls back to email / "Technician".
export const shortName = (user) => {
  const full = user?.name?.trim();
  if (!full) return user?.email || "Technician";
  const parts = full.split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export const fullName = (user) => user?.name?.trim() || user?.email || "Technician";

export const Avatar = ({ user, className = "" }) => (
  <img
    src={getAvatarUrl(user?.avatar)}
    className={`rounded-full border border-white object-cover bg-[#E2E8F0] ${className}`}
    alt=""
  />
);

// A small card anchored below `anchorRef`, rendered in a portal so it escapes
// the calendar grid's clipping. The parent owns open/close state and outside
// clicks; this only positions and renders children.
export function Popover({ anchorRef, children, width = 280 }) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const anchorCenterX = rect.left + rect.width / 2;
    // center the popover under/over the anchor, clamped to the viewport
    let left = anchorCenterX - width / 2;
    left = Math.min(Math.max(left, 8), window.innerWidth - width - 8);

    const MARGIN = 8; // gap between popover and anchor / viewport edge
    // measure the card's natural (unclamped) content height; scrollHeight
    // ignores the maxHeight cap so re-measuring can't oscillate the flip.
    const cardH = cardRef.current?.scrollHeight || 160;
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    // flip above only when there isn't room below AND there's more room above
    const openUp = cardH > spaceBelow && spaceAbove > spaceBelow;
    // how much vertical space the popover may use on the chosen side
    const maxHeight = Math.max((openUp ? spaceAbove : spaceBelow) - MARGIN, 80);

    setPos({
      top: openUp ? undefined : rect.bottom + MARGIN,
      bottom: openUp ? window.innerHeight - rect.top + MARGIN : undefined,
      left,
      openUp,
      maxHeight,
      // caret points at the anchor's center (12px caret, so -6)
      arrowLeft: Math.min(Math.max(anchorCenterX - left - 6, 12), width - 24),
    });
  }, [anchorRef, width]);

  // Callback ref: re-place (with the real card height) as soon as the card
  // mounts, and observe it so later size changes reposition too.
  const observerRef = useRef(null);
  const setCard = useCallback(
    (node) => {
      observerRef.current?.disconnect();
      cardRef.current = node;
      if (node) {
        const ro = new ResizeObserver(() => place());
        ro.observe(node);
        observerRef.current = ro;
        place();
      }
    },
    [place],
  );

  useLayoutEffect(() => {
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [place, children]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  if (!pos) return null;

  return createPortal(
    <div
      data-timeoff-popover
      className="fixed z-[60]"
      style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width, maxWidth: "90vw" }}
    >
      {/* caret pointing at the anchor. A white square covers the card's edge at
          the caret so there's no seam, with a border-colored square behind it
          drawing the outline. Mirrors ApprovalPopover. */}
      {pos.openUp ? (
        <>
          <div
            className="absolute -bottom-[7px] w-3.5 h-3.5 rotate-45 bg-[#EDEDED]"
            style={{ left: pos.arrowLeft - 1 }}
          />
          <div className="absolute -bottom-1.5 z-10 w-3 h-3 rotate-45 bg-white" style={{ left: pos.arrowLeft }} />
        </>
      ) : (
        <>
          <div className="absolute -top-[7px] w-3.5 h-3.5 rotate-45 bg-[#EDEDED]" style={{ left: pos.arrowLeft - 1 }} />
          <div className="absolute -top-1.5 z-10 w-3 h-3 rotate-45 bg-white" style={{ left: pos.arrowLeft }} />
        </>
      )}
      <div
        ref={setCard}
        className="relative overflow-y-auto rounded-lg border border-[#EDEDED] bg-white shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-2px_rgba(0,0,0,0.05)]"
        style={{ maxHeight: pos.maxHeight }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
